// DataContext.js
import React, { createContext, useContext, useState } from 'react';

const DataStateContext = createContext();
const DataSettersContext = createContext();

export const useDataState = () => {
  const context = useContext(DataStateContext);
  if (!context) {
    throw new Error("useDataState must be used within a DataProvider");
  }
  return context;
};

export const useDataSetters = () => {
  const context = useContext(DataSettersContext);
  if (!context) {
    throw new Error("useDataSetters must be used within a DataProvider");
  }
  return context;
};

export const DataProvider = ({ children }) => {
    
    // App State
    const [currentUserView, setCurrentUserView] = useState('Dashboard');
    const [zoomViewState, setZoomViewState] = useState(15)
    const [viewState, setViewState] = useState({
        latitude: 37.0,
        longitude: -100.0,
        zoom: 15,
        bearing: 0,
        pitch: 0,
      });
    const [userGroup, setUserGroup] = useState('demo_farm');
    const [selectedBucket, setSelectedBucket] = useState(null);
    const [bucketOptions, setBucketOptions] = useState([]);
    const [selectedVineyard, setSelectedVineyard] = useState(null);
    const [vineyardOptions, setVineyardOptions] = useState([]);
    const [vineyardSummaryData, setVineyardSummaryData] = useState(null);
    const [geoJsonVineDataFull, setGeoJsonVineDataFull] = useState(null);
    const [geoJsonVineData, setGeoJsonVineData] = useState(null);
    const [geoJsonVineNoDataFull, setGeoJsonVineNoDataFull] = useState(null);
    const [geoJsonVineDataImgNames, setGeoJsonVineDataImgNames] = useState(null);
    const [geoJsonVineDataImgUrls, setGeoJsonVineDataImgUrls] = useState(null);
    const [geoJsonVineNoData, setGeoJsonVineNoData] = useState(null);
    const [geoJsonBlockData, setGeoJsonBlockData] = useState(null);
    const [geoJsonVineMaturityDataFull, setGeoJsonVineMaturityDataFull] =  useState(null);
    const [geoJsonVineMaturityData, setGeoJsonVineMaturityData] = useState(null);
    const [geoJsonVineMaturityNoData, setGeoJsonVineMaturityNoData] = useState(null);
    const [geoJsonVineMaturityNoDataFull, setGeoJsonVineMaturityNoDataFull] = useState(null);
    const [geoJsonInfrastructureAssetData, setGeoJsonInfrastructureAssetData] = useState(null);
    const [user, setUser] = useState(null);
    const [userGroups, setUserGroups] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [dateOptions, setDateOptions] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedYear, setSelectedYear] = useState('2023');
    const [blockNames, setBlockNames] = useState(['All Blocks']);
    const [selectedBlockName, setSelectedBlockName] = useState(['All Blocks']);
    const [isMaturityClassClicked, setIsMaturityClassClicked] = useState(true);
    const [isInfraAssetClicked, setIsInfraAssetClicked] = useState(false);

    // Map View State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogImageUrl, setDialogImageUrl] = useState('');

    // Data caching
    const [fileInsightsDataCache, setFileInsightsDataCache] = useState(new window.Map());
    const [fileBlocksDataCache, setFileBlocksDataCache] = useState(new window.Map());

    // Landing Dashboard State
    const [landingDashboardOpen, setLandingDashboardOpen] = useState(true);
    const [clickedDashboardTableCell, setClickedDashboardTableCell] = useState({
      'row': {
        values: 'no cell selected'
      },
      'column': {
        Header: 'no cell selected'
      }
    });
    const [expandedRows, setExpandedRows] = useState([]);
    const [tableExpandedRows, setTableExpandedRows] = useState([]);

    // KPI State
    const [levelKPISelected, setLevelKPISelected] = useState('vineyards');
    const [idKPISelected, setIdKPISelected] = useState(null);
    const [productivePlantKPI, setProductivePlantKPI] = useState(null);
    const [nonProductivePlantKPI, setNonProductivePlantKPI] = useState(null);
    const [mostProductiveUnitKPI, setMostProductiveUnitKPI] = useState(null);
    const [leastProductiveUnitKPI, setLeastProductiveUnitKPI] = useState(null);
    const [plantingEfficiencyKPI, setPlantingEfficiencyKPI] = useState(null);
    const [costPerVineKPI, setCostPerVineKPI] = useState(null);
    const [revenuePerVineKPI, setRevenuePerVineKPI] = useState(null);
    const [yieldPerVineKPI, setYieldPerVineKPI] = useState(null);
    const [kpiSettingsOpen, setKpiSettingsOpen] = useState(false);
    const [kpiSettingsExpandedRows, setKpiSettingsExpandedRows] = useState([]);
    const [kpiSettingsTable, setKpiSettingsTable] = useState([]);
    const [kpiUserInputs, setKpiUserInputs] = useState([{
      'yield': 'no_data',
      'revenue': 'no_data',
      'cost': 'no_data',
    }]);
    const [plantEffectiveCapacityUtilization, setPlantEffectiveCapacityUtilization] = useState(null);
    const [pctWeakVines, setPctWeakVines] = useState(null);
    const [pctWeakCanopy, setPctWeakCanopy] = useState(null);
    const [pctWeakFruit, setPctWeakFruit] = useState(null);

    // Plant Asset Class (i.e. maturity level) Panel State
    const [isCheckedViewAssetClassOnMap, setIsCheckedViewAssetClassOnMap] = useState(false);
    const [sortLandingTableMode, setSortLandingTableMode] = useState('absolute');  // 'absolute' or 'percentage'
    const [plantAssetClasses, setPlantAssetClasses] = useState([]);
    const [plantAssetTable, setPlantAssetTable] = useState([]);

    // Map legend State
    const [showHistogram, setShowHistogram] = useState(false);
    const [sortedCategoricalHistogramLabels, setSortedCategoricalHistogramLabels] = useState([]);

    // Function to toggle the view between 'Dashboard' and 'Map'
    const toggleUserView = () => {
      setCurrentUserView(currentUserView === 'Map' ? 'Dashboard' : 'Map');
    };
    

  return (

    <DataStateContext.Provider value={{ 

        // App State
        currentUserView,
        viewState,
        zoomViewState,
        userGroup,
        selectedBucket,
        bucketOptions,
        selectedVineyard,
        vineyardOptions,
        vineyardSummaryData,
        geoJsonVineDataFull,
        geoJsonVineData,
        geoJsonVineNoDataFull,
        geoJsonVineDataImgNames,
        geoJsonVineDataImgUrls,
        geoJsonVineNoData,
        geoJsonBlockData,
        geoJsonVineMaturityDataFull,
        geoJsonVineMaturityData,
        geoJsonVineMaturityNoData,
        geoJsonVineMaturityNoDataFull,
        geoJsonInfrastructureAssetData,
        user,
        userGroups,
        dataLoaded,
        dateOptions,
        selectedDate,
        selectedYear,
        blockNames,
        selectedBlockName,

        // Map View State
        dialogOpen,
        dialogImageUrl,

        // Data caching
        fileInsightsDataCache,
        fileBlocksDataCache,

        // Landing Dashboard State
        landingDashboardOpen,
        clickedDashboardTableCell,
        sortLandingTableMode,
        expandedRows,
        tableExpandedRows,

        // KPI State
        levelKPISelected,
        idKPISelected,
        productivePlantKPI,
        nonProductivePlantKPI,
        mostProductiveUnitKPI,
        leastProductiveUnitKPI,
        plantingEfficiencyKPI,
        costPerVineKPI,
        revenuePerVineKPI,
        yieldPerVineKPI,
        kpiSettingsOpen,
        kpiSettingsTable,
        kpiUserInputs,
        plantEffectiveCapacityUtilization,
        pctWeakCanopy,
        pctWeakFruit,
        pctWeakVines,

        // Plant asset class (i.e. maturity level) panel
        isCheckedViewAssetClassOnMap,
        isMaturityClassClicked,
        isInfraAssetClicked,
        plantAssetClasses,
        plantAssetTable,

        // Map Legend State
        showHistogram,
        sortedCategoricalHistogramLabels,

        }}>

      <DataSettersContext.Provider value={{ 

        // App state
        setCurrentUserView,
        setViewState,
        setZoomViewState,
        setUserGroup,
        setSelectedBucket,
        setBucketOptions,
        setSelectedVineyard,
        setVineyardOptions,
        setVineyardSummaryData,
        setGeoJsonVineDataFull,
        setGeoJsonVineData,
        setGeoJsonVineNoDataFull,
        setGeoJsonVineDataImgNames,
        setGeoJsonVineDataImgUrls,
        setGeoJsonVineNoData,
        setGeoJsonBlockData,
        setGeoJsonVineMaturityDataFull,
        setGeoJsonVineMaturityData,
        setGeoJsonVineMaturityNoData,
        setGeoJsonVineMaturityNoDataFull,
        setGeoJsonInfrastructureAssetData,
        setUser,
        setUserGroups,
        setDataLoaded,
        setDateOptions,
        setSelectedDate,
        setSelectedYear,
        setBlockNames,
        setSelectedBlockName,

        // Map View State
        setDialogOpen,
        setDialogImageUrl,

        // Data Caching State
        setFileInsightsDataCache,
        setFileBlocksDataCache,

        // Landing Dashboard State
        setLandingDashboardOpen,
        setClickedDashboardTableCell,
        setSortLandingTableMode,
        setExpandedRows,
        setTableExpandedRows,

        // KPI State
        setLevelKPISelected,
        setIdKPISelected,
        setProductivePlantKPI,
        setNonProductivePlantKPI,
        setMostProductiveUnitKPI,
        setLeastProductiveUnitKPI,
        setPlantingEfficiencyKPI,
        setCostPerVineKPI,
        setRevenuePerVineKPI,
        setYieldPerVineKPI,
        setKpiSettingsOpen,
        setKpiSettingsTable,
        setKpiUserInputs,
        setPlantEffectiveCapacityUtilization,
        setPctWeakCanopy,
        setPctWeakFruit,
        setPctWeakVines,

        // Plant asset class (i.e. maturity level) panel
        setIsCheckedViewAssetClassOnMap,
        setIsMaturityClassClicked,
        setIsInfraAssetClicked,
        setPlantAssetClasses,
        setPlantAssetTable,

        // Map Legend State
        setShowHistogram,
        setSortedCategoricalHistogramLabels,

        }}>

        {children}

      </DataSettersContext.Provider>
    </DataStateContext.Provider>
  );
};